import { Injectable } from '@angular/core';
import { Firestore, doc, getDoc, collection, getDocs, query, limit } from 'firebase/firestore';
import { NGXLogger } from 'ngx-logger';
import { FirebaseService } from './firebase.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FirestoreConnectionService {
  private connectionTested = false;
  private connectionStatus: 'connected' | 'disconnected' | 'error' | 'unknown' = 'unknown';

  constructor(
    private firebaseService: FirebaseService,
    private logger: NGXLogger
  ) {}

  /**
   * Valida la configurazione Firebase
   */
  private validateFirebaseConfig(): boolean {
    const config = environment.firebase;
    const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
    return requiredFields.every(field => config[field as keyof typeof config] && config[field as keyof typeof config] !== '');
  }

  /**
   * Ottiene informazioni sulla configurazione Firebase
   */
  getFirebaseConfigInfo(): { isValid: boolean; projectId: string; environment: string } {
    const isValid = this.validateFirebaseConfig();
    return {
      isValid,
      projectId: environment.firebase.projectId,
      environment: environment.production ? 'production' : 'development'
    };
  }

  /**
   * Testa la connessione a Firestore
   * Ritorna anche il numero di documenti letti dalla collection di referenze (se accessibile)
   */
  async testConnection(): Promise<{ connected: boolean; error?: string; responseTime: number; documentsCount?: number }> {
    const startTime = Date.now();

    try {
      // Verifica la configurazione Firebase
      const configInfo = this.getFirebaseConfigInfo();
      if (!configInfo.isValid) {
        return {
          connected: false,
          error: 'Configurazione Firebase non valida',
          responseTime: Date.now() - startTime
        };
      }

      // Test di connessione effettivo - esegue solo una lettura
      const firestore = this.firebaseService.firestore;

      // Prova a leggere i documenti da una collection esistente (referenze)
      const collectionRef = collection(firestore, environment.firebase.collectionReferenze);
      const q = query(collectionRef);

      // Se la query riesce, la connessione funziona
      const snapshot = await getDocs(q);
      const documentsCount = snapshot.size;
      // Se arriviamo qui, la connessione è OK
      this.connectionStatus = 'connected';
      this.connectionTested = true;
      return {
        connected: true,
        responseTime: Date.now() - startTime,
        documentsCount
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Errore sconosciuto';
      this.logger.error('[FIRESTORE-CONNECTION] Errore durante il test di connessione:', errorMessage);

      // Se l'errore è di permessi, la connessione funziona ma non abbiamo i permessi
      // Consideriamo comunque la connessione OK per il test di connettività
      if (errorMessage.includes('permission') || errorMessage.includes('Permission')) {
        this.connectionStatus = 'connected';
        this.connectionTested = true;
        return {
          connected: true,
          error: 'Connessione OK ma permessi limitati',
          responseTime: Date.now() - startTime,
          documentsCount: 0
        };
      }

      // Per altri errori, la connessione non funziona
      this.connectionStatus = 'error';
      this.connectionTested = true;
      return {
        connected: false,
        error: errorMessage,
        responseTime: Date.now() - startTime
      };
    }
  }

  /**
   * Ottiene lo stato della connessione
   */
  getConnectionStatus(): 'connected' | 'disconnected' | 'error' | 'unknown' {
    return this.connectionStatus;
  }

  /**
   * Verifica se la connessione è stata testata
   */
  isConnectionTested(): boolean {
    return this.connectionTested;
  }

  /**
   * Resetta lo stato della connessione (per nuovi test)
   */
  resetConnectionStatus(): void {
    this.connectionTested = false;
    this.connectionStatus = 'unknown';
  }

  /**
   * Ottiene informazioni dettagliate sulla connessione
   */
  getConnectionInfo(): {
    status: 'connected' | 'disconnected' | 'error' | 'unknown';
    tested: boolean;
    config: { isValid: boolean; projectId: string; environment: string };
  } {
    const configInfo = this.getFirebaseConfigInfo();

    return {
      status: this.connectionStatus,
      tested: this.connectionTested,
      config: configInfo
    };
  }
}

